/**
 * ViolationImageReport - AI-powered image analysis for reporting HOA violations
 * Allows users to upload photos and get AI analysis for potential violations
 */
import { useState } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Camera, Upload, Loader2, AlertTriangle, CheckCircle,
  X, Sparkles, FileText, Eye
} from "lucide-react";

const ViolationImageReport = ({ open, onOpenChange, onViolationCreated }) => {
  const { token } = useAuth();
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [context, setContext] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a JPEG, PNG, or WebP image");
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error("Image too large. Max 50MB.");
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
    setAnalysisResult(null);
  };

  const handleAnalyze = async () => {
    if (!imageFile) return;

    try {
      setAnalyzing(true);
      const formData = new FormData();
      formData.append("file", imageFile);
      if (context) formData.append("context", context);

      const res = await axios.post(`${API}/image-analysis/analyze-violation`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        },
        timeout: 60000
      });

      setAnalysisResult(res.data);
      toast.success("Image analyzed successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Analysis failed. Please try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleReset = () => {
    setImageFile(null);
    setImagePreview(null);
    setContext("");
    setAnalysisResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="violation-image-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-violet-600" />
            AI Violation Photo Analysis
          </DialogTitle>
          <DialogDescription>
            Upload a photo and our AI will analyze it for potential HOA violations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Image Upload */}
          {!imagePreview ? (
            <label
              className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
              data-testid="image-upload-area"
            >
              <Upload className="w-10 h-10 text-muted-foreground mb-2" />
              <p className="text-sm font-medium text-muted-foreground">Click to upload a photo</p>
              <p className="text-xs text-muted-foreground mt-1">JPEG, PNG, WebP - Max 50MB</p>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleImageSelect}
                data-testid="violation-image-input"
              />
            </label>
          ) : (
            <div className="relative">
              <img
                src={imagePreview}
                alt="Uploaded"
                className="w-full max-h-64 object-contain rounded-lg border bg-muted"
                data-testid="violation-image-preview"
              />
              <Button
                variant="outline"
                size="icon"
                className="absolute top-2 right-2 h-7 w-7 bg-background/80"
                onClick={handleReset}
                data-testid="remove-image-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Context */}
          <div>
            <Label>Additional Context (Optional)</Label>
            <Textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Describe what you see or any relevant details..."
              rows={2}
              data-testid="violation-context-input"
            />
          </div>

          {/* Analyze Button */}
          {imagePreview && !analysisResult && (
            <Button
              onClick={handleAnalyze}
              disabled={analyzing}
              className="w-full"
              data-testid="analyze-violation-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing with AI...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Analyze Photo for Violations
                </>
              )}
            </Button>
          )}

          {/* Analysis Result */}
          {analysisResult && (
            <Card className="border-violet-200 bg-violet-50/30" data-testid="analysis-result">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                  AI Analysis Result
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none text-sm whitespace-pre-wrap">
                  {analysisResult.analysis}
                </div>
                <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                  <Eye className="w-3 h-3" />
                  Analyzed at {new Date(analysisResult.analyzed_at).toLocaleString()}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {analysisResult && (
            <Button variant="outline" onClick={handleReset} data-testid="analyze-another-btn">
              Analyze Another Photo
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ViolationImageReport;
